/**
 * AgentChat - Chat Interface for Harry AI Agent
 * 
 * Handles message display, input, and real-time conversation.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Send, 
  Loader2, 
  Bot, 
  User, 
  Mic, 
  MicOff, 
  RotateCcw,
  Sparkles,
  ChevronDown,
  Paperclip,
  FileText,
  Image as ImageIcon,
  XCircle
} from 'lucide-react';
import logger from '@/lib/logging';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useVoiceInput } from '@/hooks/use-voice-input';
import { getConversation, sendMessage, AgentAttachmentInput } from '@/services/api/agent-api';
import type { ChatMessage, ChatAttachment } from './types/agent-types';
import { handoutFileName, handoutTitle, isHtmlDocument } from './handout';

const MAX_ATTACHMENT_BYTES = 512 * 1024; // 512KB
const MAX_ATTACHMENTS = 5;
const MARKDOWN_EXTENSIONS = /\.(md|markdown)$/i;

interface AgentChatProps {
  conversationId: string;
  clientId?: string;
  clientName?: string;
  onConversationUpdated?: (conversationId: string, title: string) => void;
  /** A first message to send once the conversation has loaded (from the coach chat sheet). */
  initialMessage?: string;
  /** Called once the initial message has been handed to send. */
  onInitialMessageSent?: () => void;
}

const AgentChat: React.FC<AgentChatProps> = ({
  conversationId,
  clientId,
  clientName,
  onConversationUpdated,
  initialMessage,
  onInitialMessageSent,
}) => {
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  /** Set after the first load of this conversation finished (messages are in place). */
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [attachments, setAttachments] = useState<AgentAttachmentInput[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Voice input
  const { isListening, isSupported, startListening, stopListening } = useVoiceInput({
    onResult: (text) => setInputValue(prev => prev + ' ' + text),
    onError: (error) => logger.error('Voice input error:', error),
  });

  // Load conversation on mount or when ID changes
  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle scroll visibility for scroll button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const loadConversation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const conversation = await getConversation(conversationId);
      if (conversation) {
        setMessages(conversation.messages || []);
      }
    } catch (err: any) {
      logger.error('Failed to load conversation:', err);
      setError(err.message || 'Failed to load conversation');
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  // Send the initial message once, after the conversation has loaded.
  const initialSent = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialSent.current || !hasLoaded) return;
    initialSent.current = true;
    void sendText(initialMessage);
    onInitialMessageSent?.();
    // Not keyed on sendText: it reads current state and must run once per initial message.
  }, [initialMessage, hasLoaded]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSend = () => sendText(inputValue);

  const sendText = async (text: string) => {
    const trimmedMessage = text.trim();
    if (!trimmedMessage || isSending) return;

    // Optimistic update - add user message immediately
    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: trimmedMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, tempUserMessage]);
    setInputValue('');
    setIsSending(true);
    setError(null);
    setSuggestions([]);

    try {
      const response = await sendMessage({
        conversationId,
        message: trimmedMessage,
        clientId,
        attachments: attachments.length ? attachments : undefined,
        context: {
          includeAssessments: true,
          includePostureAnalyses: true,
          includePrograms: true,
        },
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        metadata: response.metadata,
      };

      setMessages(prev => {
        // Replace temp user message with actual + add assistant message
        const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
        const userMessage: ChatMessage = {
          ...tempUserMessage,
          id: `user-${Date.now()}`,
        };
        return [...withoutTemp, userMessage, assistantMessage];
      });

      // Set suggestions if provided
      if (response.suggestions?.length) {
        setSuggestions(response.suggestions);
      }
      
      // Clear attachments after successful send
      if (attachments.length) {
        setAttachments([]);
      }

      // Update conversation title if it's the first message
      if (messages.length === 0 && onConversationUpdated) {
        const newTitle = trimmedMessage.substring(0, 50) + (trimmedMessage.length > 50 ? '...' : '');
        onConversationUpdated(conversationId, newTitle);
      }
    } catch (err: any) {
      logger.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      setInputValue(trimmedMessage); // Restore the input
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachmentButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    Array.from(files).forEach(file => processFileAttachment(file));
    event.target.value = '';
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    
    event.preventDefault();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (file) {
        processFileAttachment(file);
      }
    });
  };

  const processFileAttachment = async (file: File) => {
    setUploadError(null);
    
    if (attachments.length >= MAX_ATTACHMENTS) {
      setUploadError(`Maximum of ${MAX_ATTACHMENTS} attachments per message`);
      return;
    }
    
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setUploadError(`"${file.name}" exceeds ${(MAX_ATTACHMENT_BYTES / 1024).toFixed(0)}KB limit`);
      return;
    }
    
    const baseAttachment: AgentAttachmentInput = {
      id: `${Date.now()}-${file.name}`,
      name: file.name,
      type: 'file',
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
    };
    
    try {
      if (file.type.startsWith('image/')) {
        const dataUrl = await readFileAsDataUrl(file);
        addAttachment({
          ...baseAttachment,
          type: 'image',
          dataUrl,
          preview: 'Image attachment',
        });
      } else if (file.type.startsWith('text/') || MARKDOWN_EXTENSIONS.test(file.name) || file.type === 'application/json') {
        const content = await readFileAsText(file);
        addAttachment({
          ...baseAttachment,
          type: 'text',
          content,
          preview: content.slice(0, 280),
        });
      } else {
        const dataUrl = await readFileAsDataUrl(file);
        addAttachment({
          ...baseAttachment,
          dataUrl,
          preview: 'File attachment',
        });
      }
    } catch (err) {
      logger.error('Attachment processing failed', err);
      setUploadError(`Unable to process ${file.name}`);
    }
  };

  const addAttachment = (attachment: AgentAttachmentInput) => {
    setAttachments(prev => [...prev, attachment]);
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    textareaRef.current?.focus();
  };

  const handleRetry = () => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      if (lastUserMessage) {
        setInputValue(lastUserMessage.content);
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
            <Bot className="h-5 w-5 text-accent-fg" />
          </div>
          <div>
            <h2 className="font-semibold">Harry</h2>
            <p className="text-xs text-muted">
              {clientName ? `Working with ${clientName}` : 'AI Movement Assistant'}
            </p>
          </div>
        </div>
        
        {isSending && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Thinking...</span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted" />
          </div>
        ) : messages.length === 0 ? (
          <WelcomeMessage clientName={clientName} />
        ) : (
          messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
        
        {error && (
          <div className="p-4 bg-status-deviation-surface text-status-deviation rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={handleRetry}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute bottom-32 right-8 rounded-full shadow-lg"
          onClick={scrollToBottom}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      )}

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.id}
              attachment={attachment}
              onRemove={() => removeAttachment(attachment.id)}
            />
          ))}
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="px-4 pb-2 text-xs text-status-deviation flex items-center gap-2">
          <XCircle className="h-3 w-3" />
          {uploadError}
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              {suggestion}
            </Button>
          ))}
        </div>
      )}

      {/* Input Area */}
        <div className="p-4 border-t border-line bg-surface/50">
        <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".txt,.md,.markdown,.json,.csv,.pdf,image/png,image/jpeg"
              multiple
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 shrink-0"
              onClick={handleAttachmentButtonClick}
              disabled={isSending}
              title="Add document or image"
            >
              <Paperclip className="h-5 w-5" />
            </Button>
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            placeholder="Ask Harry about clients, drills, or training programs..."
            className="min-h-[48px] max-h-[200px] resize-none"
            disabled={isSending}
            rows={1}
          />
          
          {isSupported && (
            <Button
              variant={isListening ? 'destructive' : 'outline'}
              size="icon"
              className="h-12 w-12 shrink-0"
              onClick={isListening ? stopListening : startListening}
              disabled={isSending}
            >
              {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
          )}
          
          <Button
            size="icon"
            className="h-12 w-12 shrink-0 bg-accent hover:bg-accent/90"
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
          >
            {isSending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
        
        <p className="text-xs text-muted mt-2 text-center">
          Harry can make mistakes. Always verify important information.
        </p>
      </div>
    </div>
  );
};

// Message Bubble Component
/**
 * Shows an HTML handout as a preview with a Download button instead of its source.
 * The preview iframe is fully sandboxed (no scripts, its own origin), because the
 * document is model output; there is deliberately no "open in a new tab".
 */
const HandoutPreview: React.FC<{ html: string }> = ({ html }) => {
  const title = handoutTitle(html);
  const download = () => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = handoutFileName(title);
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  return (
    <div className="space-y-2" data-testid="agent-handout">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{title}</span>
        <Button size="sm" variant="outline" onClick={download}>
          Download
        </Button>
      </div>
      <iframe
        title={title}
        srcDoc={html}
        sandbox=""
        className="h-[420px] w-full min-w-[260px] rounded-lg border border-line"
      />
    </div>
  );
};

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  
  return (
    <div className={cn('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0">
          <Bot className="h-4 w-4 text-accent-fg" />
        </div>
      )}
      
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isUser
            ? 'bg-accent text-accent-fg rounded-tr-sm'
            : 'bg-bg border border-line rounded-tl-sm'
        )}
      >
        <div className="text-sm text-fg space-y-2">
          {!isUser && isHtmlDocument(message.content) ? (
            <HandoutPreview html={message.content} />
          ) : (
            renderMessageContent(message.content)
          )}
        </div>
        
        {message.attachments && message.attachments.length > 0 && (
          <AttachmentList attachments={message.attachments} />
        )}
        
        {message.metadata?.sources && message.metadata.sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-line/50 text-xs text-muted">
            Sources: {message.metadata.sources.join(', ')}
          </div>
        )}
        
        <div className="text-xs opacity-60 mt-1">
          {formatTime(message.timestamp)}
        </div>
      </div>
      
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-surface border border-line flex items-center justify-center shrink-0">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

// Welcome Message Component
const WelcomeMessage: React.FC<{ clientName?: string }> = ({ clientName }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8">
    <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center mb-4">
      <Bot className="h-8 w-8 text-accent-fg" />
    </div>
    <h2 className="text-xl font-semibold mb-2">
      {clientName ? `Let's work on ${clientName}'s program` : 'How can I help you today?'}
    </h2>
    <p className="text-muted max-w-md">
      I can help you analyze assessments, find appropriate drills, and create 
      evidence-based training programs for your clients.
    </p>
  </div>
);

const AttachmentChip: React.FC<{
  attachment: AgentAttachmentInput;
  onRemove: () => void;
}> = ({ attachment, onRemove }) => {
  const icon =
    attachment.type === 'image' ? (
      <ImageIcon className="h-3.5 w-3.5" />
    ) : (
      <FileText className="h-3.5 w-3.5" />
    );

  return (
    <div className="flex items-center gap-2 rounded-full bg-bg px-3 py-1.5 text-xs text-fg border border-line">
      {icon}
      <span className="max-w-[120px] truncate">{attachment.name}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-muted hover:text-fg"
        aria-label="Remove attachment"
      >
        <XCircle className="h-3 w-3" />
      </button>
    </div>
  );
};

const AttachmentList: React.FC<{ attachments: ChatAttachment[] }> = ({ attachments }) => {
  return (
    <div className="mt-3 space-y-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="border border-line/70 rounded-md p-3 bg-surface/70 text-fg"
        >
          <div className="flex items-center gap-2 text-sm font-medium mb-2">
            {attachment.type === 'image' ? (
              <ImageIcon className="h-4 w-4" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            <span className="break-all">{attachment.name}</span>
          </div>
          {attachment.type === 'image' && attachment.dataUrl ? (
            <img
              src={attachment.dataUrl}
              alt={attachment.name}
              className="rounded-md max-h-48 w-auto object-contain border border-line/50"
            />
          ) : (
            <p className="text-xs text-muted whitespace-pre-wrap break-words">
              {attachment.preview || attachment.content?.slice(0, 400) || 'Attachment'}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper functions
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function renderMessageContent(text: string): React.ReactNode[] {
  if (!text) return [];

  const placeholderMap = new Map<string, { href: string; label: string }>();
  let processed = text;

  const registerLink = (href: string, label: string) => {
    const safeHref = sanitizeUrl(href);
    if (!safeHref) {
      return `${label} (${href})`;
    }
    const placeholder = `[[LINK_${placeholderMap.size}]]`;
    placeholderMap.set(placeholder, { href: safeHref, label });
    return placeholder;
  };

  processed = processed.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, label, url) =>
    registerLink(url, label)
  );

  processed = processed.replace(/<a\s+href="(https?:\/\/[^"]+)"\s*>(.*?)<\/a>/gi, (_, url, label) =>
    registerLink(url, label)
  );

  const segments = processed
    .split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[\[LINK_\d+\]\])/g)
    .filter(Boolean);

  return segments.flatMap((segment, index) => {
    if (segment.startsWith('[[LINK_')) {
      const link = placeholderMap.get(segment);
      if (!link) {
        return (
          <React.Fragment key={`link-${index}`}>
            {renderPlainText(segment, `link-${index}`)}
          </React.Fragment>
        );
      }
      return (
        <a
          key={`link-${index}`}
          href={link.href}
          target="_blank"
          rel="noreferrer"
          className="text-accent underline break-all"
        >
          {link.label}
        </a>
      );
    }

    if (segment.startsWith('**') && segment.endsWith('**')) {
      const content = segment.slice(2, -2);
      return (
        <strong key={`bold-${index}`} className="font-semibold">
          {renderPlainText(content, `bold-${index}`)}
        </strong>
      );
    }

    if (segment.startsWith('*') && segment.endsWith('*')) {
      const content = segment.slice(1, -1);
      return (
        <em key={`italic-${index}`} className="italic">
          {renderPlainText(content, `italic-${index}`)}
        </em>
      );
    }

    return (
      <React.Fragment key={`text-${index}`}>
        {renderPlainText(segment, `text-${index}`)}
      </React.Fragment>
    );
  });
}

function renderPlainText(text: string, keyPrefix: string): React.ReactNode[] {
  const lines = text.split('\n');
  return lines.flatMap((line, idx) => {
    const nodes: React.ReactNode[] = [
      <React.Fragment key={`${keyPrefix}-${idx}`}>{line}</React.Fragment>,
    ];
    if (idx < lines.length - 1) {
      nodes.push(<br key={`${keyPrefix}-br-${idx}`} />);
    }
    return nodes;
  });
}

function sanitizeUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

function formatTime(timestamp: Date | any): string {
  const date =
    timestamp instanceof Date
      ? timestamp
      : new Date(
          timestamp?.seconds ? timestamp.seconds * 1000 : timestamp || Date.now()
        );
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default AgentChat;

